import React, { useEffect, useState } from "react";
import { API_BASE } from "../../api/config";
import { modalBox, modalGrid, modalLabel, modalInput, modalPrimary } from "./FolgevertragModal";

const heute = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => (d ? d.split("-").reverse().join(".") : "–");

/**
 * Kündigung starten (Welle 81): legt einen Kündigungsvorgang an.
 * Props: vertrag ({mietvertrag_nr, name}), onClose(), onCreated(vorgang)
 */
export default function KuendigenModal({ vertrag, onClose, onCreated }) {
  const [richtung, setRichtung] = useState("mieter");
  const [schreibenDatum, setSchreibenDatum] = useState(heute());
  const [eingangAm, setEingangAm] = useState(heute());
  const [zum, setZum] = useState("");
  const [zumManuell, setZumManuell] = useState(false);
  const [grundKey, setGrundKey] = useState("eigenbedarf");
  const [grundText, setGrundText] = useState("");
  const [notiz, setNotiz] = useState("");
  const [vorschlag, setVorschlag] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState("");

  const zugang = richtung === "mieter" ? eingangAm : (eingangAm || schreibenDatum);

  useEffect(() => {
    if (!vertrag?.mietvertrag_nr || !zugang) return;
    let aktiv = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/mietverwaltung/api/kuendigung/frist-vorschlag?mietvertrag_nr=` +
          `${encodeURIComponent(vertrag.mietvertrag_nr)}&richtung=${richtung}&zugang=${zugang}`);
        const d = await res.json();
        if (!aktiv) return;
        if (!res.ok) { setFehler(d.detail || "Frist konnte nicht berechnet werden"); return; }
        setVorschlag(d);
        if (!zumManuell) setZum(d.kuendigung_zum || "");
      } catch (e) { if (aktiv) setFehler(String(e.message || e)); }
    })();
    return () => { aktiv = false; };
  }, [vertrag?.mietvertrag_nr, richtung, zugang, zumManuell]);

  const submit = async () => {
    setFehler("");
    if (richtung === "mieter" && !eingangAm) { setFehler("Bitte das Zugangsdatum der Kündigung angeben."); return; }
    if (richtung === "vermieter" && !schreibenDatum) { setFehler("Bitte das Datum des Kündigungsschreibens angeben."); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/mietverwaltung/api/kuendigung`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mietvertrag_nr: vertrag.mietvertrag_nr,
          richtung,
          schreiben_datum: schreibenDatum || null,
          eingang_am: eingangAm || null,
          kuendigung_zum: zum || null,
          frist_regel: zumManuell ? "manuell" : (vorschlag?.frist_regel || null),
          grund_key: richtung === "vermieter" ? grundKey : null,
          grund_text: richtung === "vermieter" ? grundText : null,
          notiz: notiz || null,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "Kündigung konnte nicht angelegt werden");
      onCreated?.(d);
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const radio = (val, label) => (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13.5, cursor: "pointer" }}>
      <input type="radio" name="richtung" checked={richtung === val} onChange={() => { setRichtung(val); setZumManuell(false); }} />
      {label}
    </label>
  );

  return (
    <div className="me-drawer-overlay" style={{ display: "flex", alignItems: "center",
      justifyContent: "center" }} onClick={onClose}>
      <div style={{ ...modalBox, width: "min(560px, 94vw)" }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0 }}>Kündigung starten — {vertrag.name || vertrag.mietvertrag_nr}</h3>
        <p style={{ fontSize: 13, color: "#94a3b8", margin: "8px 0 0" }}>
          Legt einen Kündigungsvorgang an. Der Vertrag bekommt den Status „Gekündigt“, alle
          weiteren Schritte (Schreiben, Übergabe, Kaution) laufen im Tab „Vorgänge“.
        </p>
        <div style={{ display: "flex", gap: 18, marginTop: 14 }}>
          {radio("mieter", "Mieter kündigt")}
          {radio("vermieter", "Vermieter kündigt")}
        </div>
        <div style={modalGrid}>
          {richtung === "mieter" ? (
            <>
              <label style={modalLabel}>Kündigung zugegangen am *
                <input type="date" style={modalInput} value={eingangAm}
                  onChange={(e) => { setEingangAm(e.target.value); setZumManuell(false); }} />
              </label>
              <label style={modalLabel}>Schreiben des Mieters vom
                <input type="date" style={modalInput} value={schreibenDatum}
                  onChange={(e) => setSchreibenDatum(e.target.value)} />
              </label>
            </>
          ) : (
            <>
              <label style={modalLabel}>Kündigungsschreiben vom *
                <input type="date" style={modalInput} value={schreibenDatum}
                  onChange={(e) => { setSchreibenDatum(e.target.value); setZumManuell(false); }} />
              </label>
              <label style={modalLabel}>Zugang beim Mieter (falls bekannt)
                <input type="date" style={modalInput} value={eingangAm}
                  onChange={(e) => { setEingangAm(e.target.value); setZumManuell(false); }} />
              </label>
            </>
          )}
          <label style={modalLabel}>Kündigung zum
            <input type="date" style={modalInput} value={zum}
              onChange={(e) => { setZum(e.target.value); setZumManuell(true); }} />
          </label>
          <div style={{ ...modalLabel, justifyContent: "flex-end", fontSize: 12.5 }}>
            {vorschlag ? (
              <>
                <span>Vorschlag: <b style={{ color: "#e2e8f0" }}>{fmtDate(vorschlag.kuendigung_zum)}</b>
                  {" "}({vorschlag.monate} Monate{vorschlag.wohndauer_jahre != null ? `, Wohndauer ${vorschlag.wohndauer_jahre} J.` : ""})</span>
                {vorschlag.widerspruch_frist && <span>Widerspruch bis {fmtDate(vorschlag.widerspruch_frist)}</span>}
                {zumManuell && <button type="button" className="me-btn me-btn-outline" style={{ alignSelf: "flex-start", padding: "2px 8px", fontSize: 12 }}
                  onClick={() => { setZumManuell(false); setZum(vorschlag.kuendigung_zum || ""); }}>Vorschlag übernehmen</button>}
              </>
            ) : <span>Frist wird berechnet…</span>}
          </div>
          {richtung === "vermieter" && (
            <>
              <label style={modalLabel}>Kündigungsgrund (§ 573 BGB)
                <select style={modalInput} value={grundKey} onChange={(e) => setGrundKey(e.target.value)}>
                  <option value="eigenbedarf">Eigenbedarf</option>
                  <option value="verwertung">Wirtschaftliche Verwertung</option>
                  <option value="pflichtverletzung">Erhebliche Pflichtverletzung</option>
                  <option value="sonstiges">Sonstiges berechtigtes Interesse</option>
                </select>
              </label>
              <label style={{ ...modalLabel, gridColumn: "1 / -1" }}>Begründung (kommt ins Schreiben)
                <textarea style={{ ...modalInput, height: 70, padding: 8, resize: "vertical" }} value={grundText}
                  onChange={(e) => setGrundText(e.target.value)} />
              </label>
            </>
          )}
          <label style={{ ...modalLabel, gridColumn: "1 / -1" }}>Notiz
            <input style={modalInput} value={notiz} onChange={(e) => setNotiz(e.target.value)} />
          </label>
        </div>
        {vorschlag?.hinweis && (
          <p style={{ fontSize: 12.5, color: "#fcd34d", margin: "10px 0 0" }}>{vorschlag.hinweis}</p>
        )}
        {fehler && <p style={{ color: "#fca5a5", fontSize: 13 }}>{fehler}</p>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
          <button className="me-btn me-btn-outline" onClick={onClose}>Abbrechen</button>
          <button className="me-btn" disabled={busy || !zum} onClick={submit} style={modalPrimary}>
            {busy ? "Legt an…" : "Kündigungsvorgang anlegen"}
          </button>
        </div>
      </div>
    </div>
  );
}
